import { Dispatch, SetStateAction, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  ListItem,
  TextField,
  Typography
} from '@mui/material';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { PrimaryButton, SecondaryButton } from '@components';
import { useUser } from '@hooks/useUser';
import { createOperationIncident } from '../../../../networkRequest/operationsService';
import Utils from '../../../../utils/utils';
import { OPERATION_INCIDENT_TYPES } from '../../../../constants';
import { Dict } from 'src/utils/types';

dayjs.extend(customParseFormat);

interface FormErrors {
  title: string | null;
  case_number: string | null;
  date: string | null;
  time: string | null;
}

interface FormData {
  title: string;
  case_number: string;
  date: string;
  time: string;
}

const defaultFormErrors: FormErrors = {
  title: null,
  case_number: null,
  date: null,
  time: null,
};

const defaultIncidentTypes: Dict<boolean> = Object.keys(OPERATION_INCIDENT_TYPES)
  .reduce((acc, name) => {
    acc[name] = false;
    return acc;
  }, {});

const incidentTypeListItems = Object.entries(OPERATION_INCIDENT_TYPES)
  .map(([name, label]) => ({ name, label }));


const CreateIncidentDialog = ({
  operationId,
  open,
  setOpen,
  onCreateIncident,
  setError,
}: {
  operationId: string;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onCreateIncident: () => Promise<void>;
  setError: Dispatch<SetStateAction<string | null>>;
}) => {
  const { user } = useUser();
  const [incidentTypes, setIncidentTypes] = useState<Dict<boolean>>(defaultIncidentTypes);
  const [formErrors, setFormErrors] = useState<FormErrors>(defaultFormErrors);
  const formRef = useRef(null);

  const close = () => {
    setIncidentTypes(defaultIncidentTypes);
    setFormErrors(defaultFormErrors);
    setOpen(false);
  };

  const handleSubmit = async (event) => {
    if (!user?.accessToken) {
      return;
    }

    event.preventDefault();
    const hasErrors = validateForm();
    if (hasErrors) return;

    const formData = new FormData(event.currentTarget);
    const {
      title,
      case_number,
      date,
      time
    } = Object.fromEntries(formData) as unknown as FormData;

    const incident_type = Object.entries(incidentTypes)
      .filter(([_, value]) => (value == true))
      .map(([key, _]) => key)
      .join(',');

    const { data, error } = await createOperationIncident({
      accessToken: user?.accessToken,
      operationId,
      params: {
        title,
        case_number,
        incident_type,
        performed_at: Utils.createTimestamp(date, time)
      }
    });

    if (!data && error) {
      setError(error);
      return;
    }

    close();
    onCreateIncident();
  }

  const validateForm = (): boolean => {
    if (!formRef.current) return false;

    const formData = new FormData(formRef.current);
    const title = formData.get('title') as string;
    const date = formData.get('date') as string;
    const time = formData.get('time') as string;

    const newFormErrors: FormErrors = {
      title: !title.length ? 'Title is required' : null,
      case_number: null,
      date: !date.length ? 'Date is required' : null,
      time: !time.length ? 'Time is required' : null,
    };

    if (date && time) {
      const timestamp = Utils.createTimestamp(date, time);
      const selectedDateTime = dayjs(timestamp);
      const now = dayjs();

      if (selectedDateTime.isAfter(now)) {
        newFormErrors.date = 'Date cannot be in the future';
        newFormErrors.time = 'Time cannot be in the future';
      }
    }

    setFormErrors(newFormErrors);
    const hasErrors = Object.values(newFormErrors).some(Boolean);
    return hasErrors;
  };

  return (
    <Dialog
      open={open}
      maxWidth='md'
      slotProps={{
        backdrop: { invisible: true },
        paper: { style: { borderRadius: 8, width: '783px' } }
      }}
    >
      <DialogTitle sx={{ padding: 3 }} variant='h5'>
        New Incident
      </DialogTitle>

      <form ref={formRef} onSubmit={handleSubmit} noValidate>
        <DialogContent sx={{ padding: '24px 24px 36px 24px' }}>
          <Grid container>

            <Grid size={12}>
              <TextField
                fullWidth
                required
                name='title'
                label='Title'
                error={!!formErrors.title}
                onBlur={validateForm}
              />
            </Grid>

            <Grid size={12} sx={{
              mt: '16px'
            }}>
              <TextField
                fullWidth
                name='case_number'
                label='Case Number'
                error={!!formErrors.case_number}
                onBlur={validateForm}
              />
            </Grid>

            <Grid size={12} container spacing={1} sx={{
              mt: '16px'
            }}>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    name='date'
                    label='Date'
                    disableFuture
                    slotProps={{
                      textField: {
                        required: true,
                        error: !!formErrors.date,
                        onBlur: () => validateForm(),
                        sx: {
                          width: '100%',
                          '& fieldset': {
                            borderRadius: '10px',
                            borderColor: '#E0E0E0', // match the border color of the rest of the mui inputs
                          },
                        },
                        slotProps: { inputLabel: { shrink: true } },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimePicker
                    name='time'
                    label='Time'
                    disableFuture
                    slotProps={{
                      textField: {
                        required: true,
                        error: !!formErrors.time,
                        onBlur: () => validateForm(),
                        sx: {
                          width: '100%',
                          '& fieldset': {
                            borderRadius: '10px',
                            borderColor: '#E0E0E0', // match the border color of the rest of the mui inputs
                          },
                        },
                        slotProps: { inputLabel: { shrink: true } },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>

            <Grid size={12} sx={{
              mt: "16px"
            }}>
              <Typography>Type of Incident</Typography>
              <FormGroup>
                <Box sx={{
                  mt: '4px',
                  display: 'flex',
                  flexFlow: { xs: 'column', sm: 'column wrap' },
                  height: 220,
                  overflow: { xs: 'scroll' }
                }}>
                  {incidentTypeListItems.map((item) => (
                    <ListItem
                      key={item.name}
                      disablePadding
                      sx={{
                        width: { sm: '50%' },
                        pl: '16px',
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={incidentTypes[item.name]}
                            onChange={() => {
                              setIncidentTypes((prev) => {
                                const _incidentTypes = { ...prev };
                                _incidentTypes[item.name] = !_incidentTypes[item.name];
                                return _incidentTypes;
                              });
                            }}
                            name={item.name}
                          />
                        }
                        label={item.label}
                      />
                    </ListItem>
                  ))}
                </Box>
              </FormGroup>
            </Grid>

          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            paddingTop: 1,
            paddingBottom: 3,
            paddingRight: 3,
            paddingLeft: 3,
          }}
        >
          <SecondaryButton action={close}>
            Cancel
          </SecondaryButton>

          <PrimaryButton type='submit'>Save</PrimaryButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateIncidentDialog;
